export const translations = {
  en: {
    nav: {
      items: [
        { label: "Platform", href: "#platform" },
        { label: "Professionals", href: "#roles" },
        { label: "Organisations", href: "#institutions" },
        { label: "Government", href: "#government" },
        { label: "About", href: "#about" },
      ],
      contact: "Contact",
      demo: "Join Krida",
    },
    hero: {
      eyebrow: "THE SPORTS WORKFORCE OS",
      h1a: "Powering the People",
      h1b: "Behind",
      accent: "Sport.",
      sub: "A unified platform to discover, verify, skill, deploy and manage the coaches, officials, performance professionals, event crews, venue teams and support workforce behind sport.",
      ctaPrimary: "Join Krida",
      ctaSecondary: "For Organisations",
      scroll: "The Platform",
      accents: ["Sport.", "Match Day.", "Tournaments."],
      telemetry: [
        { tag: "Credentialing", title: "Head Physio — Accreditation", chip: "Verified" },
        { tag: "Deployment", title: "Regional League — Round 6", chip: "Referee Confirmed" },
        { tag: "Scheduling", title: "National U-17 Camp", chip: "Calendar Synced" },
      ],
    },
    marquee: ["Verified Identity", "Credentialing", "Deployment", "Compliance", "Scheduling", "Workforce Intelligence", "Career Progression"],
    imagery: { caption: "Sport runs on the people behind it." },
    tiles: {
      chapter: "The Professionals",
      heading: "One registry. Every role that makes sport possible.",
      roles: [
        { index: "01", name: "Coach" },
        { index: "02", name: "Referee" },
        { index: "03", name: "Physiotherapist" },
        { index: "04", name: "Event Professional" },
      ],
    },
    problem: {
      chapter: "The Problem",
      h2a: "The sporting ecosystem needs more than a job portal.",
      h2b: "It needs a workforce operating system.",
      body: "Coaches, officials, physiotherapists, analysts and event professionals move between academies, leagues, tournaments and venues — often unverified, unrecorded and outside any formal system. Institutions carry the risk.",
      quote: "Better People Systems Build Better Sport.",
      caption: "Officials coordinate before kick-off.",
    },
    lifecycle: {
      chapter: "How Krida Works",
      heading: "Six stages. One continuous system.",
      stages: [
        { label: "Discover", desc: "Build and search structured pools of sports professionals by role, sport, region and availability." },
        { label: "Verify", desc: "Identity, experience, credentials, licences and certifications — checked before any deployment." },
        { label: "Skill", desc: "Training, certification and professional development pathways for the workforce." },
        { label: "Deploy", desc: "Connect professionals with roles, assignments, events and organisations." },
        { label: "Manage", desc: "Scheduling, availability, assignments and workforce operations in one system of record." },
        { label: "Progress", desc: "Verified work history, experience and career progression that travels with the professional." },
      ],
      featuresLeft: [
        "Verified identities & credentials",
        "Sports-specific professional discovery",
        "Temporary, seasonal & permanent deployment",
        "Tournament & venue workforce planning",
        "Digital scheduling & attendance",
      ],
      featuresRight: [
        "Contract, compliance & assignment workflows",
        "Performance ratings & deployment history",
        "Training, certification & upskilling",
        "Regional & international career mobility",
        "Institutional workforce analytics",
      ],
      explainers: [
        { title: "Registry & Credentialing", desc: "Identity verification, certification tracking and compliance flags maintain a living, trusted registry of sporting professionals." },
        { title: "Deployment & Scheduling", desc: "Tournament and venue workforce planning with digital scheduling, attendance and assignment management at event scale." },
        { title: "Workforce Intelligence", desc: "Workforce analytics and compliance reporting give federations and organisers a clear, current picture of their people systems." },
      ],
    },
    dashboard: {
      chapter: "Workforce Intelligence",
      heading: "See Your Sports Workforce Clearly.",
      sub: "Verified professionals by sport, role, region, certification and availability — one operational view for institutions and government.",
      nav: ["Dashboard", "Registry", "Deployments", "Calendar", "Payments", "Analytics"],
      kpis: ["Verified Professionals", "Active Deployments", "Partner Institutions"],
      barTitle: "Deployments by Role",
      donutTitle: "Verification Pipeline",
      donutCenter: "Verified",
      tableTitle: "Upcoming Deployments",
      status: { confirmed: "Confirmed", pending: "Pending" },
      chartRoles: ["Coach", "Official", "Physio", "Analyst", "Event Ops"],
      rows: [
        { name: "A. Rathore", role: "Coach", event: "National U-17 Camp", date: "Aug 04", status: "confirmed" },
        { name: "S. Menon", role: "Referee", event: "Regional League Rd. 6", date: "Aug 07", status: "pending" },
        { name: "D. Iyer", role: "Physio", event: "State Athletics Meet", date: "Aug 12", status: "confirmed" },
        { name: "K. Tan", role: "Analyst", event: "Invitational Cup", date: "Aug 18", status: "confirmed" },
      ],
    },
    band: {
      units: ["Coach", "Official", "Therapist", "Analyst", "Operations"],
      caption: "Verified · One system of record · Cross-regional",
    },
    universe: {
      eyebrow: "The Workforce Universe",
      h2: "Behind Every Game Is a Workforce.",
      sub: "Krida serves the professionals and workers whose work enables sport to function — across every sport, at every level.",
      categories: [
        "Coaches",
        "Officials",
        "Performance & Medical",
        "Team Operations",
        "Event Workforce",
        "Venue Operations",
        "Technical Staff",
        "Sports Administration",
        "Media & Content",
        "Support Services",
      ],
    },
    gameday: {
      eyebrow: "One Fixture. Eighteen Roles.",
      h2a: "You See the Game.",
      h2b: "Krida Sees the Workforce.",
      body: "Every match, tournament, academy, venue and sporting programme depends on an organised workforce. Krida brings that workforce onto one connected platform.",
      hub: "Game Day",
      roles: [
        "Coach", "Assistant Coach", "Referee", "Scorer", "Statistician", "Physiotherapist",
        "S&C Coach", "Team Manager", "Ground Staff", "Venue Manager", "Accreditation", "Security",
        "Transport", "Equipment", "Broadcast", "Media", "Event Ops", "Volunteers",
      ],
      photos: ["Match Officials", "Broadcast & Media", "Pitch-side Medical"],
    },
    profile: {
      eyebrow: "Professional Identity",
      h2: "What a Krida Professional Looks Like.",
      sub: "A verified sports workforce identity — credentials, experience, availability and work history in one record. Not merely a user account.",
      illustrative: "Illustrative profile",
      name: "Arun Kumar",
      role: "Basketball Referee",
      location: "Chennai, Tamil Nadu",
      stats: [
        { label: "Games Officiated", value: "67" },
        { label: "Tournaments", value: "12" },
        { label: "Experience", value: "5 Years" },
      ],
      meta: [
        { label: "Certification", value: "State Certified" },
        { label: "Languages", value: "Tamil / English" },
        { label: "Availability", value: "Weekends" },
      ],
      recent: "U18 State Championship",
      actions: ["View Credentials", "Check Availability"],
    },
    careers: {
      eyebrow: "Careers Beyond the Pitch",
      h2a: "Build a Career in Sport.",
      h2b: "Even If You're Not Playing It.",
      sub: "A professional sporting career extends far beyond becoming an athlete. Krida serves the breadth of the sports workforce economy.",
      categories: [
        "Coaching", "Officiating", "Performance & Sports Science", "Sports Medicine",
        "Team Operations", "Event Operations", "Venue Management", "Sports Administration",
        "Technical Operations", "Media & Content", "Sports Analytics", "Community Sport",
      ],
    },
    gallery: {
      eyebrow: "Moments From the Workforce",
      captions: ["Ground Staff", "Stewards & Security", "Volunteers", "Event Crew", "Media & Content", "Performance & Medical"],
    },
    athletes: {
      eyebrow: "The Ecosystem Around the Athlete",
      h2a: "Athletes are part of the ecosystem.",
      h2b: "Krida powers the workforce around them.",
      body: "Behind every athlete stands a team — coaches who develop them, officials who keep the contest fair, physiotherapists who keep them moving, and the operations crews who stage every fixture. Krida exists for that team.",
      ring: ["Coaches & Trainers", "Referees & Officials", "Physios & Medical", "Team Operations", "Event & Venue Crew"],
      caption: "The team behind the athlete",
    },
    registry: {
      eyebrow: "For Professionals",
      h2: "Join the registry.",
      body: "Coaches, officials, physiotherapists, analysts and event professionals — register your interest and be first in line for verification as Krida opens in your region.",
      ph: { name: "Name", email: "Email", region: "City / Region" },
      roleLabel: "Your role",
      roles: ["Coach", "Referee / Official", "Physiotherapist", "Analyst", "Event Professional"],
      submit: "Join the Registry",
      sending: "Sending…",
      successTitle: "You're on the list.",
      successBody: "Thank you — we'll reach out as verification opens in your region.",
      error: "Something went wrong. Please try again.",
    },
    forRoles: {
      chapter: "For Professionals",
      heading: "What Krida means for you",
      tabs: [
        {
          id: "coaches",
          title: "Coaches",
          prop: "Verified credentials, structured deployments and a professional record that travels with your career — across academies, leagues and regions.",
          points: ["Verified identity & certifications", "Deployment history in one record", "Contract & assignment workflows"],
        },
        {
          id: "officials",
          title: "Referees & Officials",
          prop: "Certification tracking, assignment workflows and a transparent record of every match, meet and tournament officiated.",
          points: ["Certification & grade tracking", "Structured assignment workflows", "Performance ratings & history"],
        },
        {
          id: "physiotherapists",
          title: "Physiotherapists",
          prop: "Credentialing, venue deployment and compliance managed end to end — so care professionals focus on athletes, not administration.",
          points: ["Credential & compliance verification", "Venue & camp deployments", "Digital scheduling & attendance"],
        },
        {
          id: "analysts",
          title: "Analysts",
          prop: "A verified professional identity and structured engagement with academies, leagues and tournaments that need performance intelligence.",
          points: ["Verified professional profile", "Discovery by institutions", "Seasonal & project deployment"],
        },
        {
          id: "event-professionals",
          title: "Event Professionals",
          prop: "Tournament-scale deployment with scheduling, attendance and assignment workflows built in — from single fixtures to multi-venue events.",
          points: ["Event-scale workforce planning", "Digital scheduling & attendance", "Structured assignment workflows"],
        },
      ],
    },
    institutions: {
      chapter: "For Organisations",
      heading: "Built for the institutions that run sport",
      cards: [
        { index: "01", title: "Academies & Clubs", pain: "Seasonal staffing, unverified credentials and fragmented records across programmes.", stages: ["Verify", "Discover", "Deploy"] },
        { index: "02", title: "Tournament & Event Organizers", pain: "Event-scale workforce planning, scheduling and compliance across venues and fixtures.", stages: ["Deploy", "Manage", "Progress"] },
        { index: "03", title: "Federations & Associations", pain: "Build the workforce your sport depends on — structured professional pools, credential oversight and opportunity creation.", stages: ["Verify", "Skill", "Progress"] },
        { index: "04", title: "Schools & Universities", pain: "Verified coaches and officials for inter-school and collegiate sport, without the administrative burden.", stages: ["Discover", "Verify", "Deploy"] },
        { index: "05", title: "Venues & Stadiums", pain: "Event-day workforce — operations, accreditation, technical and support teams, coordinated in one place.", stages: ["Deploy", "Manage", "Progress"] },
        { index: "06", title: "Government Sports Bodies", pain: "Verified registries and formalized workforce systems for public sporting missions.", stages: ["Verify", "Skill", "Progress"] },
      ],
      usecase: {
        h2: "Running a State Tournament?",
        sub: "You may need:",
        numbers: [
          { n: "28", label: "Officials" },
          { n: "14", label: "Scorers & Timekeepers" },
          { n: "10", label: "Physiotherapists" },
          { n: "6", label: "Competition Managers" },
          { n: "24", label: "Venue Staff" },
          { n: "40", label: "Event Personnel" },
          { n: "60", label: "Volunteers" },
          { n: "12", label: "Logistics" },
        ],
        closing: "Krida brings the workforce together.",
        workflow: [
          "Create requirement",
          "Find eligible professionals",
          "Credentials checked",
          "Availability confirmed",
          "Assignment accepted",
          "Workforce deployed",
          "Work history updated",
        ],
      },
    },
    government: {
      chapter: "Government & Policy",
      h2: "Sports workforce infrastructure for public missions",
      body: "Krida complements existing athlete-registration and competition systems as the workforce layer — giving ministries and public bodies verified infrastructure to develop and deploy sporting professionals at scale.",
      quote: "A verified workforce is the foundation every sporting mission stands on.",
      points: [
        { title: "Workforce Visibility", desc: "Understand the available sports workforce by sport, district, role, qualification, certification, experience and availability." },
        { title: "Workforce Development", desc: "Identify skill shortages, certification requirements, regional gaps and training demand across the ecosystem." },
        { title: "Deployment", desc: "See where professionals are being utilised across programmes, venues and events." },
        { title: "Outcomes", desc: "Track people registered, verified, trained and deployed — with workforce participation and geographic distribution, as registries connect." },
      ],
    },
    about: {
      chapter: "About Krida",
      h2: "Better People Systems Build Better Sport.",
      body: "CYRA Krida is the Sports Workforce OS — verified identity, credentialing, deployment and compliance for the professionals who make sport possible. Where sport meets systems, Krida stands with the people behind the game.",
      principles: ["Verified Identity", "Compliance", "Mobility"],
    },
    contact: {
      chapter: "Contact",
      h2: "Contact us",
      sub: "Tell us about your institution, or simply say hello — the Krida team will get back to you.",
      ph: { name: "Name", organization: "Organization", role: "Role", email: "Email", message: "Message" },
      submit: "Send Message",
      sending: "Sending…",
      successTitle: "Message received.",
      successBody: (name, org) => `Thank you, ${name}. The Krida team will get back to you shortly.`,
      sendAnother: "Send another message",
      error: "Something went wrong. Please try again.",
    },
    closing: {
      eyebrow: "CYRA Krida",
      h2: "Sport runs on people. Krida connects the workforce behind it.",
      cta: "Join Krida",
    },
    footer: {
      tagline: "The Sports Workforce OS",
      powered: "Human Capital. Reinvented.",
      navigate: "Navigate",
      contact: "Contact",
      rights: "© 2026 CYRA Krida",
      motto: "Powering the People Behind Sport.",
    },
  },
};
